import { createFileRoute, getRouteApi, useRouter } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { t } from '@lingui/core/macro';
import { settingsService } from '@/services/settings';
import { useToast } from '@/hooks/useToast';
import { showFlashMessage } from '@/utils/flashMessageUtils';
import EmailPreferences from '@/components/Settings/EmailPreferences';
import EhashEmailPreferences from '@/components/Settings/EhashEmailPreferences';
import type { EmailPreferences as EmailPreferencesType } from '@/types/settings';

const settingsRoute = getRouteApi('/_app/settings');

interface EmailSettingsSearch {
  ehash?: string;
  unsubscribed?: string;
}

export const Route = createFileRoute('/_app/settings/email')({
  validateSearch: (search: Record<string, unknown>): EmailSettingsSearch => ({
    ehash: typeof search.ehash === 'string' ? search.ehash : undefined,
    unsubscribed:
      typeof search.unsubscribed === 'string' ? search.unsubscribed : undefined,
  }),
  component: EmailPreferencesPage,
});

function EmailPreferencesPage() {
  const { ehash, unsubscribed } = Route.useSearch();
  const navigate = Route.useNavigate();

  // An emailed ehash stands in for a session: show that mailbox's settings
  // even when signed out (or signed into a different account).
  if (ehash) {
    return (
      <EhashEmailPreferences
        ehash={ehash}
        unsubscribed={unsubscribed}
        onUnsubscribedHandled={() => navigate({ search: { ehash }, replace: true })}
      />
    );
  }

  return <AuthenticatedEmailPreferencesPage />;
}

function AuthenticatedEmailPreferencesPage() {
  const router = useRouter();
  const settings = settingsRoute.useLoaderData();
  const { showToast } = useToast();
  const formKey = settings.user.formKey;

  // Email preferences mutation
  const updateEmailPreferencesMutation = useMutation({
    mutationFn: ({
      preferences,
      formKey,
    }: {
      preferences: EmailPreferencesType;
      formKey: string;
    }) => settingsService.updateEmailPreferences(preferences, formKey),
    onSuccess: async () => {
      // Check for flash message from backend (with delay for fetch redirects)
      showFlashMessage(showToast);
      await router.invalidate();
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  const handlePreferenceChange = (field: keyof EmailPreferencesType, value: boolean) => {
    // Get current preferences from loader data
    const updatedPreferences = {
      ...settings.emailPreferences,
      [field]: value,
    };

    // Submit mutation
    updateEmailPreferencesMutation.mutate({
      preferences: updatedPreferences,
      formKey,
    });
  };

  return (
    <EmailPreferences
      preferences={settings.emailPreferences}
      formKey={formKey}
      onPreferenceChange={handlePreferenceChange}
      isUpdating={updateEmailPreferencesMutation.isPending}
    />
  );
}
